import React, { useEffect, useRef, useState } from "react";

import WizardHeader from "../../components/portal/WizardHeader";
import StepIndicator from "../../components/portal/StepIndicator";
import CountryServicesStep from "../../components/portal/CountryServicesStep";
import CompanyDetailsStep from "../../components/portal/CompanyDetailsStep";
import AIAssistantStep from "../../components/portal/AIAssistantStep";
import GenerateDocumentsStep from "../../components/portal/GenerateDocumentsStep";

import type {
  ChatMessage,
  ICompanyDetails,
  CountryCode,
  WizardStep,
  WizardStepId,
} from "../../store/@types/portal";
import { emptyCompanyDetails } from "../../store/@types/portal";
import { get_countries } from "../../service/api/locations";
import { get_services } from "../../service/api/service";
import {
  ai_assistant_chat,
  aiErrorMessage,
  create_compliance_application,
  generate_document,
} from "../../service/api/ai";
import { useAppDispatch, useAppSelector } from "../../store";
import { COUNTRIES } from "../../constants/countryData";
import type { AIChatTurn } from "../../store/@types/ai";
import type { ICreateComplianceApplication } from "../../store/@types/compliance";
import type { IDocument } from "../../store/@types/document";

const STEPS: WizardStep[] = [
  { id: "country", label: "Country & Services" },
  { id: "company", label: "Company Details" },
  { id: "ai", label: "AI Assistance" },
  { id: "generate", label: "Generate Documents" },
];

const ComplianceWizard: React.FC = () => {
  const dispatch = useAppDispatch();
  const countries = useAppSelector(({ locations }) => locations.countries);
  const countriesLoading = useAppSelector(({ locations }) => locations.loading);
  const services = useAppSelector(({ service }) => service.services);
  const servicesLoading = useAppSelector(({ service }) => service.loading);
  const [currentStepId, setCurrentStepId] = useState<WizardStepId>("country");
  const [countryId, setCountryId] = useState("");
  const [countryCode, setCountryCode] = useState<CountryCode | "">("");
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [companyDetails, setCompanyDetails] =
    useState<ICompanyDetails>(emptyCompanyDetails);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [askBusy, setAskBusy] = useState(false);
  const [creatingApplication, setCreatingApplication] = useState(false);
  const creatingApplicationRef = useRef(false);
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [generatingDocuments, setGeneratingDocuments] = useState(false);
  const [generatedDocuments, setGeneratedDocuments] = useState<
    IDocument[] | null
  >(null);
  const generatingDocumentsRef = useRef(false);
  const countriesRequested = useRef(false);
  const servicesRequested = useRef(false);

  useEffect(() => {
    if (countries.length === 0 && !countriesRequested.current) {
      countriesRequested.current = true;
      void get_countries(dispatch);
    }
  }, [countries.length, dispatch]);

  useEffect(() => {
    if (services.length === 0 && !servicesRequested.current) {
      servicesRequested.current = true;
      void get_services(dispatch, 100, 0);
    }
  }, [dispatch, services.length]);

  const handleCountryChange = (id: string) => {
    const country = countries.find((item) => String(item.id) === id);
    const knownCountry = COUNTRIES.find(
      (item) => item.name.toLowerCase() === country?.name.toLowerCase(),
    );

    setCountryId(id);
    setCountryCode(knownCountry?.code ?? "");
    // Services are country-specific, so clear any prior selection
    // whenever the country changes.
    setSelectedServiceIds([]);
    setCompanyDetails((previous) => ({
      ...previous,
      contact_phone: "",
    }));
  };

  const handleToggleService = (serviceId: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId],
    );
  };

  const handleCompanyDetailsChange = (
    field: keyof ICompanyDetails,
    value: ICompanyDetails[keyof ICompanyDetails],
  ) => {
    setCompanyDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateApplication = async () => {
    if (creatingApplicationRef.current) return;

    const application: ICreateComplianceApplication = {
      selected_country: Number(countryId),
      selected_services: selectedServiceIds.map(Number),
      ...companyDetails,
    };

    creatingApplicationRef.current = true;
    setCreatingApplication(true);
    try {
      const createdApplication =
        await create_compliance_application(application);
      if (createdApplication) {
        setApplicationId(createdApplication.id);
        setCurrentStepId("ai");
      }
    } finally {
      creatingApplicationRef.current = false;
      setCreatingApplication(false);
    }
  };

  const handleGenerateDocuments = async () => {
    if (applicationId === null || generatingDocumentsRef.current) return;

    generatingDocumentsRef.current = true;
    setGeneratingDocuments(true);
    try {
      const documents = await generate_document(applicationId);
      if (documents !== null) setGeneratedDocuments(documents);
    } finally {
      generatingDocumentsRef.current = false;
      setGeneratingDocuments(false);
    }
  };

  const handleSendMessage = async (content: string) => {
    if (askBusy) return;

    const history: AIChatTurn[] = messages.map((message) => ({
      role: message.role === "assistant" ? "model" : "user",
      text: message.content,
    }));
    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: "user",
      content,
    };
    setMessages((prev) => [...prev, userMessage]);
    setAskBusy(true);

    try {
      const reply = await ai_assistant_chat(content, history);
      const assistantMessage: ChatMessage = {
        id: `${Date.now()}-assistant`,
        role: "assistant",
        content: reply,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const assistantMessage: ChatMessage = {
        id: `${Date.now()}-assistant-error`,
        role: "assistant",
        content: aiErrorMessage(
          error,
          "The assistant could not reply right now. Please try again.",
        ),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } finally {
      setAskBusy(false);
    }
  };

  return (
    <div className="pb-20">
      <section className="py-12 md:py-24 lg:py-32 bg-gradient-to-br from-[#0B1526] via-[#0B1526] to-[#0F2A20]">
        <WizardHeader className="pt-20" />
      </section>

      <div className="px-4">
        <StepIndicator steps={STEPS} currentStepId={currentStepId} />
      </div>

      <div className="mx-auto mt-8 max-w-6xl px-4 sm:px-0">
        {currentStepId === "country" && (
          <CountryServicesStep
            countryId={countryId}
            countries={countries}
            services={services}
            countriesLoading={countriesLoading}
            servicesLoading={servicesLoading}
            selectedServiceIds={selectedServiceIds}
            onCountryChange={handleCountryChange}
            onToggleService={handleToggleService}
            onContinue={() => setCurrentStepId("company")}
          />
        )}

        {currentStepId === "company" && countryId && (
          <CompanyDetailsStep
            countryCode={countryCode}
            countryName={
              countries.find((country) => String(country.id) === countryId)
                ?.name ?? ""
            }
            details={companyDetails}
            creatingApplication={creatingApplication}
            onChange={handleCompanyDetailsChange}
            onBack={() => setCurrentStepId("country")}
            onContinue={() => {
              void handleCreateApplication();
            }}
          />
        )}

        {currentStepId === "ai" && countryId && (
          <AIAssistantStep
            messages={messages}
            busy={askBusy}
            onSendMessage={(content) => {
              void handleSendMessage(content);
            }}
            onBack={() => setCurrentStepId("company")}
            onContinue={() => setCurrentStepId("generate")}
          />
        )}

        {currentStepId === "generate" && countryId && (
          <GenerateDocumentsStep
            countryCode={countryCode}
            countryName={
              countries.find((country) => String(country.id) === countryId)
                ?.name ?? ""
            }
            services={services.filter((service) =>
              selectedServiceIds.includes(String(service.id)),
            )}
            companyDetails={companyDetails}
            generatingDocuments={generatingDocuments}
            generatedDocuments={generatedDocuments}
            onBack={() => setCurrentStepId("ai")}
            onGenerateDocuments={() => {
              void handleGenerateDocuments();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ComplianceWizard;
